import { getDatabase, ref, get, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { templateLookup } from '../Helpers/maintenanceSchedule';
import type { MaintenancePlan } from '../Helpers/maintenanceCostUtils';
import type { Building } from '../types/protocols';
import type { ComponentLifetime, TemplatesByType } from '../Pages/Förvaltning/TemplatesEditor/types';
import { draftFromActivePlan } from '../Pages/Förvaltning/MaintenanceDraft/utils/draftFromActive';
import { costAttachmentPaths } from '../Pages/Förvaltning/MaintenanceDraft/utils/costSettings';
import { tombstoneAttachmentDiff } from './fileDeletionApi';
import { recordActivity } from './activityApi';
import type { Attachment } from '../types/attachments';

/** "Utgå från aktiv plan" or "Börja från scratch" */
export type DraftStart = 'fromActive' | 'scratch';

export interface CreateDraftOptions {
    buildingKey: string;
    start: DraftStart;
    /** Needed for `fromActive` only */
    building?: Building | null;
    componentLifetimes?: Record<string, ComponentLifetime>;
    maintenanceTemplates?: TemplatesByType;
}

const parsePlan = (data: unknown): MaintenancePlan | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (!d.components || typeof d.components !== 'object') return null;
    if (!d.metadata || typeof d.metadata !== 'object') return null;
    return data as MaintenancePlan;
};

/**
 * Write a new draft at `maintenance/drafts/{b}`.
 *
 * `scratch` writes an empty draft that the draft page fills from the building
 * and the templates. `fromActive` seeds it with the active plan's decisions
 * (`draftFromActivePlan`), read fresh here; without an active plan it is `scratch`.
 */
export const createMaintenanceDraft = async (options: CreateDraftOptions): Promise<void> => {
    const { buildingKey, start, building, componentLifetimes, maintenanceTemplates } = options;
    const user = getAuth().currentUser;
    if (!user) throw new Error('Du måste vara inloggad');

    const db = getDatabase();
    const now = new Date();
    let draft: MaintenancePlan = {
        components: {},
        metadata: { savedAt: formatDateForStorage(now), buildingKey }
    };

    if (start === 'fromActive') {
        if (!building || !componentLifetimes || !maintenanceTemplates) {
            throw new Error('Byggnadens data är inte laddad. Försök igen.');
        }
        const active = parsePlan((await get(ref(db, `maintenance/active/${buildingKey}`))).val());
        if (active) {
            draft = draftFromActivePlan({
                active,
                building,
                componentLifetimes,
                maintenanceTemplates,
                findTemplate: templateLookup(maintenanceTemplates),
                now
            });
        }
    }

    draft.metadata.createdBy = user.uid;
    try {
        await set(ref(db, `maintenance/drafts/${buildingKey}`), draft);
        recordActivity('maintenancePlan.updated', { buildingKey, targetKey: buildingKey });
    } catch (error) {
        console.error('Error creating maintenance draft:', error);
        throw new Error('Utkastet kunde inte skapas. Försök igen.');
    }
};

/** Lifetimes and templates by component type, as the maintenance pages build them from the types context. */
export const maintenanceConfigFromTypes = (
    typeKeys: string[],
    getLifetime: (type: string) => ComponentLifetime | null | undefined,
    getTemplates: (type: string) => TemplatesByType[string]
): { componentLifetimes: Record<string, ComponentLifetime>; maintenanceTemplates: TemplatesByType } => {
    const componentLifetimes: Record<string, ComponentLifetime> = {};
    const maintenanceTemplates: TemplatesByType = {};
    typeKeys.forEach(type => {
        const lifetime = getLifetime(type);
        if (lifetime) componentLifetimes[type] = lifetime;
        const templates = getTemplates(type);
        if (templates.length > 0) maintenanceTemplates[type] = templates;
    });
    return { componentLifetimes, maintenanceTemplates };
};

/**
 * Tombstones the cost attachments a draft save dropped, but only those no plan
 * still holds: copying activities shares attachments between components, and
 * activation carries them into the active plan and its archives (never
 * deleted). `draftAfter` is the draft as saved; the active plan and archives
 * are read fresh.
 *
 * Best effort: a missed tombstone leaves an orphaned blob and must never block
 * the save.
 */
export const tombstoneUnreferencedCostAttachments = async (
    buildingKey: string,
    entityType: string,
    dropped: Record<string, Attachment>,
    draftAfter: Pick<MaintenancePlan, 'components'>
): Promise<void> => {
    if (Object.keys(dropped).length === 0) return;
    try {
        const db = getDatabase();
        const [activeSnapshot, archiveSnapshot] = await Promise.all([
            get(ref(db, `maintenance/active/${buildingKey}`)),
            get(ref(db, `maintenance/archive/${buildingKey}`))
        ]);
        const inUse = costAttachmentPaths(draftAfter);
        const plans: unknown[] = [activeSnapshot.val(), ...Object.values(archiveSnapshot.val() ?? {})];
        plans.forEach(plan => {
            const parsed = parsePlan(plan);
            if (parsed) costAttachmentPaths(parsed).forEach(path => inUse.add(path));
        });

        const unreferenced: Record<string, Attachment> = {};
        Object.entries(dropped).forEach(([key, attachment]) => {
            if (attachment.storagePath && !inUse.has(attachment.storagePath)) unreferenced[key] = attachment;
        });
        await tombstoneAttachmentDiff(entityType, unreferenced, undefined);
    } catch (error) {
        console.warn('Could not tombstone removed cost attachments:', error);
    }
};
